import { ReactElement, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { buildRatioForceScatter, DataMode } from './auxSensorChannels';

interface AuxRatioForcePlotProps {
  gridded: Record<string, (number | null)[]> | undefined;
  circumference: number;
  /** Min linear speed (m/s) — samples below are masked (shared slider). */
  threshold: number;
  dataMode?: DataMode;
  isLoading?: boolean;
}

/**
 * Slip-vs-tractive-force scatter for the two wheel sensors. Y is the slip ratio
 * (wheel speed 1 / wheel speed 2), X is the contact-patch force (rider power /
 * ground speed, in N). Low-speed samples — where force/ratio blow up — are masked
 * by the shared min-speed threshold. A dashed line marks ratio = 1 (agreement).
 */
export default function AuxRatioForcePlot({
  gridded,
  circumference,
  threshold,
  dataMode = 'all',
  isLoading = false,
}: AuxRatioForcePlotProps): ReactElement {
  const { points } = useMemo(
    () => buildRatioForceScatter(gridded, circumference, threshold, dataMode),
    [gridded, circumference, threshold, dataMode],
  );

  const chartSeries = useMemo(
    () => [{ name: 'Wheel 1 / Wheel 2', type: 'scatter' as const, data: points }],
    [points],
  );

  const options = useMemo(
    () => ({
      chart: {
        type: 'scatter' as const,
        height: 380,
        animations: { enabled: false },
        toolbar: {
          show: true,
          tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
        },
        zoom: { enabled: true, type: 'xy' as const },
      },
      colors: ['#8B5CF6'],
      markers: { size: 3, fillOpacity: 0.5, strokeWidth: 0 },
      xaxis: {
        type: 'numeric' as const,
        tickAmount: 8,
        title: { text: 'Contact Patch Force (N)', style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: string) => `${parseFloat(value).toFixed(0)}` },
      },
      yaxis: {
        title: { text: 'Wheel Speed 1 / Wheel Speed 2', style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: number) => (value != null ? value.toFixed(3) : '') },
      },
      grid: { borderColor: '#E5E7EB', strokeDashArray: 5 },
      annotations: {
        yaxis: [{
          y: 1,
          borderColor: '#9CA3AF',
          strokeDashArray: 6,
          label: { text: 'ratio = 1', style: { color: '#6B7280', background: '#F9FAFB' } },
        }],
      },
      tooltip: {
        shared: false,
        intersect: true,
        x: { formatter: (value: number) => `${value.toFixed(1)} N` },
        y: { formatter: (value: number) => (value != null ? value.toFixed(3) : '') },
      },
    }),
    [],
  );

  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
      <h2 className="cr-mb-2 cr-text-lg cr-font-semibold cr-text-blue-900">
        Slip Ratio vs Contact Patch Force
      </h2>
      {isLoading ? (
        <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-gap-2 cr-text-gray-400">
          <div
            className="cr-h-6 cr-w-6 cr-animate-spin cr-rounded-full cr-border-2
              cr-border-cirrusBlue-200 cr-border-t-cirrusBlue-400"
          />
          <span className="cr-text-sm">Loading…</span>
        </div>
      ) : points.length === 0 ? (
        <div className="cr-flex cr-h-32 cr-items-center cr-justify-center cr-text-center cr-text-sm cr-text-gray-400">
          No samples above {threshold.toFixed(1)} m/s (needs two wheel sensors + power).
        </div>
      ) : (
        <ReactApexChart options={options} series={chartSeries} type="scatter" height={380} />
      )}
    </div>
  );
}
